import { useState, type FormEvent } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { BrandLockup } from '@/components/app/BrandLockup';
import { BRAND } from '@/lib/brand-assets';

// Development-stage site-wide gate. Not a real security boundary — it's a
// single shared password to keep casual visitors out while the product
// isn't publicly launched yet. Remove this whole gate (and its import in
// App.tsx) once the app is ready to go live.
const DEV_ACCESS_PASSWORD = 'cmc-pass12$$$';
const STORAGE_KEY = 'cmc_dev_access_unlocked';

function hasStoredAccess(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function ComingSoonGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState(hasStoredAccess);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  if (unlocked) return <>{children}</>;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (password === DEV_ACCESS_PASSWORD) {
      try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* private browsing, etc. */ }
      setUnlocked(true);
      return;
    }
    setError('That password isn\'t right. Check your invite email, or get in touch.');
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: BRAND.cream }}
    >
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8">
          <BrandLockup size={28} variant="light" />
        </div>

        <h1
          className="font-display text-3xl sm:text-4xl font-bold mb-3"
          style={{ color: BRAND.inkNavy }}
        >
          Coming soon
        </h1>
        <p className="text-base mb-10" style={{ color: BRAND.ink, opacity: 0.7 }}>
          CoverMyClass is in development and not yet open to the public.
          We'll be launching soon.
        </p>

        {!showPasswordForm ? (
          <button
            type="button"
            onClick={() => setShowPasswordForm(true)}
            className="text-sm underline underline-offset-2 hover:opacity-70 transition-opacity"
            style={{ color: BRAND.navy700 }}
          >
            Already invited? Sign in here
          </button>
        ) : (
          <form onSubmit={handleSubmit} className="text-left bg-white/60 rounded-xl border border-black/10 p-6 space-y-4">
            <div className="flex items-center gap-2 justify-center mb-1">
              <Lock size={14} style={{ color: BRAND.navy700 }} />
              <span className="text-sm font-semibold" style={{ color: BRAND.inkNavy }}>
                Invited access
              </span>
            </div>
            <div>
              <label htmlFor="dev-access-password" className="sr-only">Password</label>
              <div className="relative">
                <Input
                  id="dev-access-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter your access password"
                  autoFocus
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" className="w-full" disabled={!password}>
              Continue
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
